import { IGroup } from '@fluentui/react';
import { concat, groupBy, map, reduce } from 'lodash';
import { GroupByOption } from 'component/Search/types';
import { applyGroupCollapseBehavior } from './applyGroupCollapseBehavior';

/**
 * Takes a list of items and groups them based on the content from the
 * provided groupByOption. It also applies the group collapse behavior.
 */
export const groupByKeyFromItem = <T,>(
  itemList: T[],
  groupByOption: GroupByOption,
  isAllGroupsCollapsed: boolean,
  collapsedGroups: any[]
) => {
  // Get our group keys from the item list via either:
  // the onGroupBy iteratee or the groupByOption key.
  const itemListGroupByKey = groupBy(itemList, item => {
    if (typeof groupByOption?.onGroupBy === 'function') {
      return groupByOption?.onGroupBy(item);
    }

    return item?.[groupByOption?.key];
  });

  // Note: I generally dislike using reducers as they're hard to understand at a glance,
  // but in this case, it's the best way to calculate the start index for each group.
  const groupList = reduce(
    itemListGroupByKey,
    (accumulatedGroupList: IGroup[], currentGroup, key) => {
      // Calculate the start index for the current group.
      const startIndex =
        // If the current group is the first group, the start index is 0.
        accumulatedGroupList.length <= 0
          ? 0
          : // Otherwise, the start index is the previous group's start index plus
            // the previous group's count.
            accumulatedGroupList[accumulatedGroupList.length - 1].startIndex +
            accumulatedGroupList[accumulatedGroupList.length - 1].count;

      const group: IGroup = {
        key,
        name: key,
        startIndex,
        count: currentGroup.length,
      };

      // Add the group to the accumulated group list.
      // I normally would not do this in a mutable way, but this array
      // is created exclusively for this reducer and not accessed elsewhere
      // until the reducer is complete.
      accumulatedGroupList.push(group);

      return accumulatedGroupList;
    },
    // The accumulatedGroupList starts as an empty array.
    []
  );

  // Apply the group collapse behavior.
  const groupListWithCollapseBehavior = applyGroupCollapseBehavior(
    groupList,
    isAllGroupsCollapsed,
    collapsedGroups
  );

  return groupListWithCollapseBehavior;
};
